// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';
import type { BoardShare } from '../types';

interface Props {
  boardId: string;
  onClose: () => void;
}

export default function ShareBoard({ boardId, onClose }: Props) {
  const state = useAppState();
  const dispatch = useDispatch();
  const [shares, setShares] = useState<BoardShare[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const board = state.boards.find((b) => b.id === boardId);

  useEffect(() => {
    api.getBoardShares(boardId).then(({ shares }) => {
      setShares(shares);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [boardId]);

  const handleShare = async () => {
    if (!email.trim()) return;
    try {
      const share = await api.shareBoardWithEmail(boardId, email.trim(), role);
      setShares([...shares, share]);
      setEmail('');
      setMsg('Shared successfully!');
      setTimeout(() => setMsg(''), 3000);
    } catch (e: any) {
      setMsg(e.message || 'Failed to share');
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    await api.removeBoardShare(boardId, shareId);
    setShares(shares.filter((s) => s.id !== shareId));
  };

  const handleTogglePrivacy = async () => {
    const newPrivate = board?.is_private ? 0 : 1;
    await api.updateBoard(boardId, { is_private: newPrivate } as any);
    dispatch({ type: 'UPDATE_BOARD', id: boardId, data: { is_private: newPrivate } });
  };

  const getRoleBadgeClass = (r: string) => {
    if (r === 'admin') return 'role-badge admin';
    if (r === 'editor') return 'role-badge editor';
    return 'role-badge viewer';
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ position: 'relative', maxWidth: 500 }}>
        <button className="modal-close" onClick={onClose}>&#10005;</button>
        <h2>Share Board</h2>
        <p style={{ color: '#676879', fontSize: 13, marginBottom: 16 }}>
          {board?.name} - {board?.is_private ? 'Private' : 'Public to workspace'}
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <button
            onClick={handleTogglePrivacy}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 13,
              background: board?.is_private ? '#f0f0ff' : '#fff0f0',
              color: board?.is_private ? '#6161ff' : '#e2445c',
              border: 'none', cursor: 'pointer',
            }}
          >
            {board?.is_private ? 'Make public to workspace' : 'Make private'}
          </button>
        </div>

        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Share with people</div>
        <div className="invite-form" style={{ marginTop: 0 }}>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address..."
            onKeyDown={(e) => e.key === 'Enter' && handleShare()}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            style={{ padding: '8px', borderRadius: 6, border: '1px solid #e6e9ef', fontSize: 13 }}
          >
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button onClick={handleShare}>Share</button>
        </div>

        {msg && (
          <div style={{
            fontSize: 13, marginTop: 8,
            color: msg.includes('Failed') || msg.includes('permission') ? '#e2445c' : '#00c875',
          }}>
            {msg}
          </div>
        )}

        {/* Copy board link */}
        <div style={{
          marginTop: 16, padding: '12px 14px', background: '#f6f7fb', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Share via link</div>
            <div style={{ fontSize: 12, color: '#676879', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {window.location.origin + '/?board=' + boardId}
            </div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin + '/?board=' + boardId);
              setLinkCopied(true);
              setTimeout(() => setLinkCopied(false), 2000);
            }}
            style={{
              padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              background: linkCopied ? '#00c875' : '#0073ea', color: 'white',
              border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.2s',
            }}
          >
            {linkCopied ? 'Copied!' : 'Copy link'}
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>People with access</div>
          {loading ? (
            <div style={{ color: '#676879', fontSize: 13 }}>Loading...</div>
          ) : shares.length === 0 ? (
            <div style={{ color: '#676879', fontSize: 13 }}>No one else has access yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {shares.map((share) => (
                <div
                  key={share.id}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: '#f6f7fb', borderRadius: 6,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14 }}>{share.user_name || share.email}</div>
                      <div style={{ fontSize: 12, color: '#676879' }}>{share.email}</div>
                    </div>
                    <span className={getRoleBadgeClass(share.role)}>{share.role}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveShare(share.id)}
                    style={{ color: '#e2445c', fontSize: 13, padding: '4px 8px', borderRadius: 4, cursor: 'pointer', border: 'none', background: 'none' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
